import { Box, Button, HStack, Text, useDisclosure } from '@chakra-ui/react';
import E911EmergencySharpIcon from '@gamma/ui/icons/E911EmergencySharpIcon';
import FlagSharpIcon from '@gamma/ui/icons/FlagSharpIcon';
import HandymanSharpIcon from '@gamma/ui/icons/HandymanSharpIcon';
import ReportSharpIcon from '@gamma/ui/icons/ReportSharpIcon';
import SearchSharpIcon from '@gamma/ui/icons/SearchSharpIcon';
import { Modal } from '@gamma/ui/overlay';
import { ErrorMessage, GraphQLReqStatus } from '@gamma/ui/progress';
import { i18n } from '@investigator/shared/localization';
import { ChatGPTToolTip } from '../../../../../../../../../../../entities/components';
import {
  DetailsGrid,
  DetailsGridRow,
} from '../../../../../../../../../../components/DetailsGrid';
import {
  SessionSummaryData,
  SessionSummaryHeaders,
} from '../../../../../../../../../../queries';

export interface SessionSummaryProps {
  sessionSummaryData?: SessionSummaryData;
  error?: ErrorMessage;
  loading: boolean;
}

export const SessionSummary = ({
  sessionSummaryData,
  error,
  loading,
}: SessionSummaryProps) => {
  const { detection } = i18n.pages.detections;
  const { sessionSummary } = i18n.pages.alertDetails;

  const {
    isOpen: sessionSummaryIsOpen,
    onOpen: sessionSummaryOnOpen,
    onClose: sessionSummaryOnClose,
  } = useDisclosure();

  const headerIcons = {
    ioc: FlagSharpIcon,
    alert_summary: ReportSharpIcon,
    unusual_findings: SearchSharpIcon,
    attack_tactics: HandymanSharpIcon,
    alert_beacons: E911EmergencySharpIcon,
  } satisfies { [K in SessionSummaryHeaders]: unknown };

  return error || loading ? (
    <GraphQLReqStatus error={error} loading={loading} />
  ) : (
    <>
      <Button
        px={2}
        marginTop={2}
        height="auto"
        variant="pill"
        w="fit-content"
        colorScheme="gray"
        whiteSpace="initial"
        data-testid="activity-summary-modal-button"
        size="sm"
        leftIcon={<ChatGPTToolTip boxSize={4} />}
        onClick={sessionSummaryOnOpen}
      >
        {sessionSummary.analyzeActivities}
      </Button>
      <Modal
        size="xl"
        isOpen={sessionSummaryIsOpen}
        onClose={sessionSummaryOnClose}
        title={
          <Box display="flex" flexDir="row">
            <ChatGPTToolTip boxSize={6} />
            <Text marginLeft={2}>{sessionSummary.alertConnectionInsights}</Text>
          </Box>
        }
        body={
          sessionSummaryData ? (
            <DetailsGrid templateColumns="1fr" gridRowGap={0}>
              {Object.values(SessionSummaryHeaders).map(
                (header, index, headers) => {
                  const showBorder = index !== headers.length - 1;
                  const borderStyles = {
                    borderBottom: '0.5px solid',
                    borderBottomColor: 'gray',
                  };
                  const rowStyles = {
                    ...(showBorder ? borderStyles : {}),
                    alignItems: 'start',
                  };
                  const HeaderIcon = headerIcons[header] ?? FlagSharpIcon;
                  return (
                    <HStack style={rowStyles} paddingY={4} key={header}>
                      <DetailsGridRow
                        title={
                          <HStack w={240}>
                            <Box style={{ paddingTop: '5px' }}>
                              <HeaderIcon size="sm" marginEnd={3} />
                            </Box>
                            <Text>{sessionSummary.headers[header]}</Text>
                          </HStack>
                        }
                      >
                        <Box>
                          {sessionSummaryData[header].map((text: string, i) => (
                            <Text key={`${header}-${i}`}>{text}</Text>
                          ))}
                        </Box>
                      </DetailsGridRow>
                    </HStack>
                  );
                },
              )}
            </DetailsGrid>
          ) : (
            <p>{sessionSummary.dataUnavailable}</p>
          )
        }
        footer={
          <Button
            variant="solid"
            colorScheme="gray"
            onClick={sessionSummaryOnClose}
          >
            {detection.close}
          </Button>
        }
      />
    </>
  );
};
